import React from "react";
import type { ReactNode } from "react";
import { useAuth } from 'tecrmi_components/useAuth';
import { setGlobalAccessTokenFn } from '@utils/httpClient';
import { useExtraction } from "@hooks/extraction/useExtraction";
import { LoadingScreen } from "@xplatform/design-system";
import { useDoctype } from "@src/hooks/flow/useDoctype";
import { ApplicationContext, type IApplicationContextType } from "./ApplicationContext";
import { setAccessToken, clearAccessToken, setAccessTokenRefresher } from "@utils/tokenStore";

interface IApplicationContextProviderProps {
    children: ReactNode;
}

export const ApplicationProvider: React.FC<IApplicationContextProviderProps> = ({ children }) => {
    const { getValidAccessToken, currentSession, loading } = useAuth();
    const [tokenReady, setTokenReady] = React.useState(false);

    // Set global token accessor so all HttpClient instances can use BeeHive token
    React.useEffect(() => {
        setGlobalAccessTokenFn(getValidAccessToken);
        setAccessTokenRefresher(getValidAccessToken);
    }, [getValidAccessToken]);

    // Cache the token in memory for the WebSocket clients, which need it
    // synchronously on every reconnect, and signal that API calls may go
    // out. It is deliberately not persisted: a bearer token in
    // localStorage is readable by any script on the page.
    React.useEffect(() => {
        const storeToken = async () => {
            const token = await getValidAccessToken();
            if (token) {
                setAccessToken(token);
                setTokenReady(true);
            }
        };
        if (currentSession) {
            storeToken();
        } else {
            clearAccessToken();
            setTokenReady(false);
        }
    }, [currentSession, getValidAccessToken]);

    const extractionState = useExtraction();
    const doctypeState = useDoctype();

    if (loading || (currentSession && !tokenReady)) {
        return <LoadingScreen message="Checking authentication..." />;
    }

    if (!currentSession) {
        return <>{children}</>;
    }

    const contextValue: IApplicationContextType = {
        extraction: extractionState,
        doctype: doctypeState,
    };

    return (
        <ApplicationContext.Provider value={contextValue}>
            {children}
        </ApplicationContext.Provider>
    );
};

